import { ElementRef } from '@angular/core';
import { TTimelineConfiguration, TTimelineModel } from './timeline.types';
import * as d3 from 'd3';
import moment from 'moment';
import { MS_PER_DAY, TTimelineDateRangeDTO } from '@monorepo/types';
import { getTotalDaysInTimeline } from './timeline.utils';

// Container
// Margin to the draw for spacing
//
export type TDrawModel = {
  domRef: HTMLDivElement;
  width: number;
  height: number;
  innerWidth: number;
  defsIds: {
    arrow: string;
    checkered: string;
    'pattern-zig-zag': string;
    'mask-zig-zag': string;
    'pattern-mesh': string;
    'mask-mesh': string;
    'pattern-diagonal-stripes': string;
  };
  layout: {
    headerHeight: number;
    entityHeight: number;
    stackHeights: [number, number]; // Height percent distributed over entity height. Sum should not be more than 100
    widthOfDay: number;
    offsetBottom: number;
    margin: {
      right: number;
      left: number;
    };
  };
  coordinates: {
    startTime: number;
    endTime: number;
  };
  scales: {
    time: d3.ScaleTime<number, number, never>;
    entity: d3.ScaleLinear<number, number, never>;
  };
  timelineModel: TTimelineModel;
  hideCalendarHoliday?: boolean;
};

export const TIMELINE_D3 = {
  createDrawModel: (
    renderElm: ElementRef<HTMLDivElement>,
    timelineModel: TTimelineModel,
    configuration: TTimelineConfiguration,
    viewDateRange?: TTimelineDateRangeDTO,
  ): TDrawModel => {
    const refElm = renderElm.nativeElement;
    const marginLocation = { right: 0, left: timelineModel.hideLeftPanel ? 0 : 150 };

    // Calculate Width and height of the timeline container
    const dimentions = refElm.getBoundingClientRect();
    const width = dimentions.width;

    let innerWidth: number;
    if (viewDateRange) {
      const naturalDays = (Date.parse(viewDateRange.to) - Date.parse(viewDateRange.from)) / MS_PER_DAY + 1;
      const viewportInnerWidth = width - marginLocation.right - marginLocation.left;
      const pixelsPerDay = viewportInnerWidth / naturalDays;

      innerWidth = pixelsPerDay * getTotalDaysInTimeline(timelineModel);
    } else {
      innerWidth = width - marginLocation.right - marginLocation.left;
    }
    const height = window.innerHeight - dimentions.top;

    let entityHeight = 40; // Deafult for TNA Board
    let offset = 10;
    let stackHeightsInPercent = [70, 30];
    switch (configuration.renderType) {
      case 'LL_BOARD':
        entityHeight = 70; // Deafult for TNA Board
        stackHeightsInPercent = [50, 40];
        offset = 10;
        break;
      case 'TNA_BOARD':
        entityHeight = 60; // Deafult for TNA Board
        offset = 15;
        break;
      case 'HL_BOARD':
        entityHeight = 100;
        stackHeightsInPercent = [100, 0];
        offset = 10;
        break;
      case 'CAPACITY_BOARD':
        entityHeight = 250;
        stackHeightsInPercent = [90, 10];
        offset = 10;
        break;
      case 'PLANNING_BOARD':
        entityHeight = 50;
        stackHeightsInPercent = [100, 0];
        offset = 10;
        break;
      case 'ORDER_TIMELINE_VIEW_BOARD':
        entityHeight = 70;
        stackHeightsInPercent = [70, 0];
        offset = 10;
        break;
      default:
        break;
    }

    const stackHeights = stackHeightsInPercent.map((v) => (v * (entityHeight - 2 * offset)) / 100) as [number, number];
    // ============================================================

    // Scale
    const timeScale = d3
      .scaleTime()
      .domain([
        moment.utc(timelineModel.timeline[0].date).valueOf(),
        moment.utc(timelineModel.timeline[timelineModel.timeline.length - 1].date).valueOf(),
      ])
      .range([0, innerWidth]);

    const entityScale = d3
      .scaleLinear()
      .domain([0, timelineModel.comptedEntities.totalEntities * entityHeight])
      .range([0, timelineModel.comptedEntities.totalEntities]);

    // Calulate width of a day
    const startTimeline = moment.utc(timelineModel.timeline[0].date);
    const endTimeline = moment.utc(timelineModel.timeline[0].date).add(1, 'd');
    const widthOfDay = timeScale(endTimeline.valueOf()) - timeScale(startTimeline.valueOf());

    // Cordicate of start and end date
    const startTime = timeScale(moment.utc(timelineModel.timeline[0]['date']).valueOf());
    const endTime = timeScale(moment.utc(timelineModel.timeline[timelineModel.timeline.length - 1]['date']).valueOf());

    return {
      domRef: refElm,
      width,
      innerWidth,
      height,
      hideCalendarHoliday: configuration.hideCalendarHolidays || false,
      defsIds: {
        arrow: `arrow-${configuration.renderType}`,
        checkered: `checkered-${configuration.renderType}`,
        'pattern-zig-zag': `pattern-zig-zag-${configuration.renderType}`,
        'mask-zig-zag': `mask-zig-zag-${configuration.renderType}`,
        'pattern-mesh': `pattern-mesh-${configuration.renderType}`,
        'mask-mesh': `mask-mesh-${configuration.renderType}`,
        'pattern-diagonal-stripes': 'pattern-diagonal-stripes',
      },
      layout: {
        entityHeight,
        stackHeights,
        offsetBottom: offset,
        headerHeight: 60,
        margin: marginLocation,
        widthOfDay,
      },
      coordinates: {
        startTime,
        endTime,
      },
      timelineModel,

      scales: {
        time: timeScale,
        entity: entityScale,
      },
    };
  },
};
